/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useEffect, useRef, useState } from 'react';
import Immutable from 'immutable';

import {
  Container,
  FormCheckbox,
  FormCheckboxes,
  FormikWrapper,
  FormSelect,
  FormTextField,
  Icon,
  TableRender,
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';

// Block implementations
import BlockCTA from '../../implementations/Block/CTABlock';
import BlockText from '../../implementations/Block/TextBlock';

// Node implementations
import NodeArticleTeaser from '../../implementations/Node/Article/Teaser';
import NodeDocumentTeaser from '../../implementations/Node/Document/Teaser';
import NodePartnerTeaser from '../../implementations/Node/Partner/Teaser';
import NodeVacancyTeaser from '../../implementations/Node/Vacancy/Teaser';
import NodeProjectTeaser from '../../implementations/Node/Project/Teaser';
import NodeEventPage from '../../implementations/Node/Event';

// Profile implementations
import DefaultProfileTeaser from '../../implementations/Profile/Default/Teaser';

// Taxonomy implementations
import TaxonomyThemeTeaser from '../../implementations/Taxonomy/Theme/Teaser';

// Paragraph implementations
import ParagraphText from '../../implementations/Paragraph/Text';
import ParagraphQuote from '../../implementations/Paragraph/Quote';
import ParagraphInformation from '../../implementations/Paragraph/Information';
import ParagraphFAQ from '../../implementations/Paragraph/FAQ';
import ParagraphDownloads from '../../implementations/Paragraph/Downloads';
import ParagraphUSP from '../../implementations/Paragraph/USP';
import ParagraphUSPSubparagraph from '../../implementations/Paragraph/USPSubparagraph';
import ParagraphTextImageGrid from '../../implementations/Paragraph/TextImageGrid';
import ParagraphTextMedia from '../../implementations/Paragraph/TextMedia';

import './style.scss';

const spacings = [2, 4, 8, 12, 16, 20, 24, 32, 36, 40, 48, 56, 64, 72, 96, 120, 144, 192, 256, 384];

// ---------------------------------------------------------------------------
// Mock entities
// ---------------------------------------------------------------------------

const mockArticleEntity = Immutable.fromJS({
  nid: [{ value: 1 }],
  uuid: [{ value: 'article-uuid-1' }],
  langcode: [{ value: 'nl' }],
  title: [{ value: 'Netbeheerbedrijven investeren in duurzame energie-infrastructuur' }],
  body: [{
    summary: 'Een uitgebreide samenvatting van het artikel over investeringen in duurzame energie en de toekomst van het Nederlandse netwerk.',
    value: '<p>Volledige tekst</p>',
    processed: '<p>Volledige tekst</p>',
  }],
  field_publish_date: [{ value: '2024-03-01' }],
  field_media_item: [],
  field_themes: [],
  field_article_type: [{ entity: { name: [{ value: 'Nieuws' }] } }],
  field_media_category: [],
  created: [{ value: '2024-03-01T00:00:00+00:00' }],
  paths: { nl: '/nieuws/netbeheerbedrijven-investeren' },
});

const mockDocumentEntity = Immutable.fromJS({
  nid: [{ value: 2 }],
  uuid: [{ value: 'doc-uuid-1' }],
  langcode: [{ value: 'nl' }],
  title: [{ value: 'Jaarverslag Energiesector 2023' }],
  body: [{
    summary: 'Het jaarverslag beschrijft de activiteiten en resultaten van het afgelopen jaar.',
    value: '<p>Volledige tekst</p>',
    processed: '<p>Volledige tekst</p>',
  }],
  field_publish_date: [{ value: '2024-01-15' }],
  field_media_item: [],
  field_themes: [],
  field_media_category: [],
  created: [{ value: '2024-01-15T00:00:00+00:00' }],
  paths: { nl: '/publicaties/jaarverslag-2023' },
});

const mockMagazineEntity = Immutable.fromJS({
  nid: [{ value: 3 }],
  uuid: [{ value: 'magazine-uuid-1' }],
  langcode: [{ value: 'nl' }],
  title: [{ value: 'Energie Magazine Editie 12' }],
  body: [{
    summary: 'De twaalfde editie van ons magazine met de laatste inzichten over de energietransitie.',
    value: '<p>Volledige tekst</p>',
    processed: '<p>Volledige tekst</p>',
  }],
  field_publish_date: [{ value: '2024-02-01' }],
  field_media_item: [],
  field_themes: [],
  field_media_category: [{ entity: { name: [{ value: 'Magazine' }] } }],
  created: [{ value: '2024-02-01T00:00:00+00:00' }],
  paths: { nl: '/magazine/editie-12' },
});

const mockPartnerEntity = Immutable.fromJS({
  title: [{ value: 'Alliander N.V.' }],
  field_media_item: [],
  field_link: [{ title: 'Bezoek website', uri: 'https://www.alliander.com', alias: '' }],
  body: [{ processed: '<p>Alliander is de grootste netbeheerder in Nederland en verzorgt het transport van elektriciteit en gas voor miljoenen klanten.</p>' }],
  field_location: [{
    entity: {
      field_address: [{ address_line1: 'Utrechtseweg 68', postal_code: '6812 AH', locality: 'Arnhem' }],
      field_postal_address: [],
    },
  }],
  field_telephone: [{ value: '088 012 0000' }],
  field_email_address: [{ value: 'info@alliander.com' }],
});

const mockVacancyEntity = Immutable.fromJS({
  title: [{ value: 'Senior Projectleider Energietransitie' }],
  field_hours_minimum: [{ value: '32' }],
  field_hours_maximum: [{ value: '40' }],
  field_education_level: [{ value: 'HBO/WO' }],
  field_publish_date: [{ value: '2024-03-01' }],
  field_link: [{ uri: '/vacature/senior-projectleider' }],
});

const mockProjectEntity = Immutable.fromJS({
  title: [{ value: 'Slim Netwerk Limburg' }],
  field_media_item: [],
  body: [{ summary: 'Een innovatief project voor de digitalisering van het energienetwerk in Limburg, met focus op duurzaamheid en efficiëntie.' }],
});

const mockEventEntity = Immutable.fromJS({
  title: [{ value: 'Jaarlijkse Netbeheerder Conferentie 2024' }],
  field_date: [{ value: '2024-09-15T13:00:00' }],
  body: [{ processed: '<p>De jaarlijkse conferentie voor alle netbeheerders en stakeholders in de energiesector. Een dag vol kennis, netwerken en inspiratie.</p>' }],
  field_media_item: [],
  field_link: [{ title: 'Aanmelden', alias: '/evenementen/conferentie-2024', uri: '/evenementen/conferentie-2024' }],
  field_labels: [],
  field_status: [],
  field_type: [{ entity: { name: [{ value: 'Congres' }] } }],
  field_target_audiences: [],
  field_location: [],
});

const mockProfileEntity = Immutable.fromJS({
  field_full_name: [{ value: 'Marie van den Berg' }],
  field_description: [{ processed: '<p>Marie heeft 15 jaar ervaring in de energiesector en is gespecialiseerd in de energietransitie.</p>' }],
  field_email_address: [{ value: 'marie@netbeheernederland.nl' }],
  field_function: [{ entity: { name: [{ value: 'Programmamanager Energietransitie' }] } }],
  field_media_item: [],
  path: [{ alias: '/profiel/marie-van-den-berg' }],
  field_telephone: [{ value: '070 311 9511' }],
});

const mockThemeEntity = Immutable.fromJS({
  name: [{ value: 'Energietransitie' }],
  description: [{ value: '<p>Alles over de transitie naar duurzame energie en de cruciale rol van netbeheerders in deze omslag.</p>' }],
  paths: { nl: '/thema/energietransitie' },
  field_media_item: [],
});

const mockTextBlockEntity = Immutable.fromJS({
  body: [{ processed: '<p>Dit is een voorbeeld van een <strong>tekst blok</strong>. Hier kan opgemaakte HTML-inhoud worden weergegeven, inclusief <a href="/">links</a>, lijsten en meer.</p>' }],
});

const mockCTAEntity = Immutable.fromJS({
  info: [{ value: 'Blijf op de hoogte van de energiesector' }],
  body: [{ processed: '<p>Schrijf u in voor onze nieuwsbrief en ontvang het laatste nieuws over netbeheer en de energietransitie direct in uw inbox.</p>' }],
  field_link: [{ title: 'Aanmelden nieuwsbrief', uri: '/nieuwsbrief', alias: '/nieuwsbrief' }],
  field_link_secondary: [{ title: 'Meer informatie', uri: '/over-ons', alias: '/over-ons' }],
  field_media: [],
});

const mockParagraphTextEntity = Immutable.fromJS({
  field_title: [{ value: 'Over Netbeheer Nederland' }],
  field_body: [{ processed: '<p>Netbeheer Nederland is de brancheorganisatie van de regionale netbeheerders van elektriciteit en gas in Nederland.</p><ul><li>Alliander</li><li>Coteq</li><li>Enduris</li><li>Enexis</li><li>Rendo</li><li>Stedin</li><li>Westland Infra</li></ul><p>De netbeheerders werken samen aan:</p><ol><li>Een betrouwbaar energienetwerk voor alle huishoudens en bedrijven</li><li>De aansluiting van duurzame energiebronnen zoals zonne- en windenergie</li><li>De verduurzaming van het gasnet en de warmtetransitie</li></ol><ol><li><strong>Meer aansluitingen op één kabel:</strong><br />maak stuurbare laadpalen de norm, elke netbewuste laadpaal is één woning extra. Maak daarbij netbewuste nieuwbouw aantrekkelijker voor investerende partijen zoals bouwers, woningcorporaties en laadpaalondernemers.</li><li><strong>Meer grip op de energierekening:</strong><br />door invoering van netbewuste (tijdsafhankelijke) tarieven voor kleinverbruikers wordt de energierekening voor zo\'n tweederde van de huishoudens flink lager. Dat komt omdat ze kunnen profiteren van de daluren. Daarmee kunnen we piekbelasting voorkomen en komt er ruimte op het net vrij voor andere ontwikkelingen.</li><li><strong>Een betaalbaar energiesysteem:</strong><br />door de uitrol van warmtenetten en de inzet van groen gas te versnellen, wordt het elektriciteitsnet ontlast en worden onnodige kosten voor verzwaring van het elektriciteitsnet voorkomen. Grootschalige uitrol is nú nodig, maar we missen in het coalitieakkoord de middelen die nodig zijn om dit op te schalen.</li><li><strong>Sneller bouwen:</strong><br />energieplanologie, snellere juridische procedures en voldoende ruimte zorgen ervoor dat de energie-infrastructuur voor de gebouwde omgeving sneller kan worden gerealiseerd.</li></ol>' }],
  field_link: [{ title: 'Lees meer over ons', uri: '/over-ons', alias: '/over-ons' }],
  field_link_secondary: [{ title: 'Contact opnemen', uri: '/contact', alias: '/contact' }],
});

const mockParagraphTextMediaEntity = Immutable.fromJS({
  field_label: [{ value: 'Energietransitie' }],
  field_title: [{ value: 'Werken aan het net van de toekomst' }],
  field_body: [{ processed: '<p>Netbeheerders investeren volop in de uitbreiding en verduurzaming van het energienet. Dit vraagt om een gecoördineerde aanpak.</p><ul><li>Uitbreiding van het hoogspanningsnet</li><li>Plaatsing van nieuwe transformatorstations</li><li>Vervanging van verouderde kabels en leidingen</li></ul><p>De planning voor de komende jaren bestaat uit:</p><ol><li>Inventarisatie van knelpunten per regio</li><li>Samenwerking met gemeenten en provincies</li><li>Gefaseerde uitvoering van werkzaamheden</li></ol><ol><li><strong>Meer aansluitingen op één kabel:</strong><br />maak stuurbare laadpalen de norm, elke netbewuste laadpaal is één woning extra. Maak daarbij netbewuste nieuwbouw aantrekkelijker voor investerende partijen zoals bouwers, woningcorporaties en laadpaalondernemers.</li><li><strong>Meer grip op de energierekening:</strong><br />door invoering van netbewuste (tijdsafhankelijke) tarieven voor kleinverbruikers wordt de energierekening voor zo\'n tweederde van de huishoudens flink lager. Dat komt omdat ze kunnen profiteren van de daluren. Daarmee kunnen we piekbelasting voorkomen en komt er ruimte op het net vrij voor andere ontwikkelingen.</li><li><strong>Een betaalbaar energiesysteem:</strong><br />door de uitrol van warmtenetten en de inzet van groen gas te versnellen, wordt het elektriciteitsnet ontlast en worden onnodige kosten voor verzwaring van het elektriciteitsnet voorkomen. Grootschalige uitrol is nú nodig, maar we missen in het coalitieakkoord de middelen die nodig zijn om dit op te schalen.</li><li><strong>Sneller bouwen:</strong><br />energieplanologie, snellere juridische procedures en voldoende ruimte zorgen ervoor dat de energie-infrastructuur voor de gebouwde omgeving sneller kan worden gerealiseerd.</li></ol>' }],
  field_link: [{ title: 'Bekijk onze projecten', uri: '/projecten', alias: '/projecten' }],
  field_link_secondary: [],
  field_alignment: [{ value: 'right' }],
  field_aspect_ratio: [],
  field_media_item: [{
    bundle: 'image',
    entity: {
      field_caption: [{ value: 'Monteurs werken aan het elektriciteitsnet' }],
    },
    styles: {},
  }],
});

const mockParagraphQuoteEntity = Immutable.fromJS({
  field_name: [{ value: 'Koen Bogers' }],
  field_body: [{ value: 'De energietransitie is de grootste uitdaging voor ons netwerk in de afgelopen decennia. Samen met alle partners in de sector kunnen we dit realiseren voor een duurzame toekomst.' }],
  field_label: [{ value: 'Directeur, Netbeheer Nederland' }],
  field_media_item: [],
});

const mockParagraphInformationEntity = Immutable.fromJS({
  field_title: [{ value: 'Wist u dat?' }],
  field_body: [{ processed: '<p>Nederland heeft meer dan <strong>200.000 kilometer</strong> aan elektriciteitsleidingen en <strong>135.000 kilometer</strong> aan gasleidingen. Samen zorgen de netbeheerders voor een betrouwbaar energienetwerk voor alle huishoudens en bedrijven.</p>' }],
  field_link: [{ title: 'Feiten en cijfers', alias: '/feiten-en-cijfers', uri: '/feiten-en-cijfers' }],
});

const mockParagraphFAQEntity = Immutable.fromJS({
  field_title: [{ value: 'Veelgestelde Vragen' }],
  field_body: [{ processed: '<p>Antwoorden op de meest gestelde vragen over netbeheer en de energietransitie.</p>' }],
  field_faq: [
    {
      target_id: 1,
      entity: {
        title: [{ value: 'Wat doet een netbeheerder?' }],
        body: [{ processed: '<p>Een netbeheerder beheert het elektriciteits- en gasnet in zijn regio. Hij zorgt voor onderhoud, vervanging en uitbreiding van het netwerk om iedereen van energie te voorzien.</p>' }],
        field_media_item: [],
        field_link: [],
      },
    },
    {
      target_id: 2,
      entity: {
        title: [{ value: 'Hoe lang duurt het aanleggen van een netaansluiting?' }],
        body: [{ processed: '<p>De doorlooptijd hangt af van de complexiteit van de aansluiting en kan variëren van enkele weken tot meer dan een jaar voor grote industriële projecten.</p>' }],
        field_media_item: [],
        field_link: [],
      },
    },
    {
      target_id: 3,
      entity: {
        title: [{ value: 'Wat is netcongestie?' }],
        body: [{ processed: '<p>Netcongestie treedt op wanneer er meer vraag is naar transportcapaciteit dan het netwerk kan leveren. Dit komt steeds vaker voor door de toename van zonnepanelen en windmolens.</p>' }],
        field_media_item: [],
        field_link: [{ title: 'Lees meer over netcongestie', alias: '/netcongestie', uri: '/netcongestie' }],
      },
    },
  ],
});

const mockParagraphDownloadsEntity = Immutable.fromJS({
  field_title: [{ value: 'Publicaties' }],
  field_body: [{ processed: '<p>Download onze meest recente publicaties en rapporten.</p>' }],
  field_downloads: [
    {
      title: 'Jaarverslag 2023',
      file_size: 4567890,
      mime_type: 'application/pdf',
      entity: {
        field_caption: [{ value: 'Jaarverslag 2023 - Netbeheer Nederland' }],
        field_media_document: [{ source: '/files/jaarverslag-2023.pdf', filename: 'jaarverslag-2023.pdf' }],
      },
    },
    {
      title: 'Visie op de Energietransitie',
      file_size: 2345678,
      mime_type: 'application/pdf',
      entity: {
        field_caption: [],
        field_media_document: [{ source: '/files/visie-energietransitie.pdf', filename: 'visie-energietransitie.pdf' }],
      },
    },
  ],
});

const mockParagraphUSPEntity = Immutable.fromJS({
  field_title: [{ value: 'Waarom Netbeheer Nederland?' }],
  field_body: [{ processed: '<p>Wij vertegenwoordigen de netbeheerders en behartigen hun belangen in Den Haag en Brussel voor een betrouwbaar en duurzaam energienetwerk.</p>' }],
  field_link: [{ title: 'Meer over ons', alias: '/over-ons', uri: '/over-ons' }],
  field_link_secondary: [{ title: 'Word lid', alias: '/lidmaatschap', uri: '/lidmaatschap' }],
  field_paragraphs: [],
});

const mockParagraphUSPSubparagraphEntity = Immutable.fromJS({
  field_title: [{ value: 'Betrouwbaar netwerk' }],
  field_body: [{ processed: '<p>Een netwerk dat dag en nacht beschikbaar is voor alle huishoudens en bedrijven in Nederland.</p>' }],
  field_media_item: [],
});

const mockParagraphTextImageGridEntity = Immutable.fromJS({
  field_title: [{ value: 'Onze infrastructuur' }],
  field_body: [{ processed: '<p>Het Nederlandse energienetwerk bestaat uit honderden kilometers aan kabels en leidingen die Nederland van energie voorzien.</p>' }],
  field_link: [],
  field_link_secondary: [],
  field_media_items: [],
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DesignSystem: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);

  const [materialIconNames, setMaterialIconNames] = useState<string[]>([]);
  const [customIconNames, setCustomIconNames] = useState<string[]>([]);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const style = window.getComputedStyle(ref.current);
    const materialIcons = style.getPropertyValue('--material-icon-names');
    const customIcons = style.getPropertyValue('--custom-icon-names');

    setMaterialIconNames(materialIcons.split(',').map((icon) => icon.trim()));
    setCustomIconNames(customIcons.split(',').map((icon) => icon.trim()));
  }, []);

  return (
    <article className="DesignSystem" ref={ref}>
      <div className="Paragraph">
        <Container>
          <h1>Design System</h1>
        </Container>
      </div>

      <div className="Paragraph">
        <Container>
          <h2>Form elements</h2>
          <div className="DesignSystem__forms">
            <FormikWrapper
              initialValues={{
                word: '',
                agreed_with_statement: false,
                poison: '',
                poison_2: [],
                poison_3: [],
              }}
            >
              <FormTextField
                title="Your favorite word"
                required
                description="This is a test input."
                placeholder="This is a placeholder"
                fieldName="word"
              />
              <FormSelect
                title="Pick your poison"
                required
                description="This is a test input."
                placeholder="Select a value..."
                fieldName="poison"
                options={{
                  beer: 'Beer',
                  wine: 'Wine',
                  rum: 'Rum',
                  whisky: 'Whisk(e)y',
                }}
                defaultValue="wine"
              />
              <FormSelect
                title="Pick your poisons"
                required
                description="This is a test input."
                placeholder="Select some values..."
                multiple
                fieldName="poison_3"
                options={{
                  beer: 'Beer',
                  wine: 'Wine',
                  rum: 'Rum',
                  whisky: 'Whisk(e)y',
                }}
                defaultValue="wine"
              />
              <FormCheckbox
                title="I agree with the statement"
                required
                description="By checking the above box, you agree with the statement."
                fieldName="agreed_with_statement"
              />
              <FormCheckboxes
                title="Pick your poison"
                required
                description="Please select your poison."
                fieldName="poison_2"
                options={{
                  beer: 'Beer',
                  wine: 'Wine',
                  rum: 'Rum',
                  whisky: 'Whisk(e)y',
                  __other__: 'Anders',
                }}
                otherLabel="Anders"
              />
            </FormikWrapper>
          </div>
        </Container>
      </div>

      <div className="Paragraph">
        <Container>
          <h2>Icons</h2>
          <h3>Material icons</h3>
          <ul className="DesignSystem__icons">
            {materialIconNames.map((iconName) => (
              <li key={`icon--${iconName}`}>
                <Icon name={iconName} flair="outlined" />
                {iconName}
              </li>
            ))}
          </ul>

          <h3>Custom icons</h3>
          <ul className="DesignSystem__icons">
            {customIconNames.map((iconName) => (
              <li key={`icon--${iconName}`}>
                <Icon name={iconName} />
                {iconName}
              </li>
            ))}
          </ul>
        </Container>
      </div>

      {/* ================================================================ */}
      {/* BLOCKS */}
      {/* ================================================================ */}

      <div className="Paragraph">
        <Container>
          <h2>Block: Text</h2>
        </Container>
        <BlockText entity={mockTextBlockEntity as any} />
      </div>

      <div className="Paragraph">
        <Container>
          <h2>Block: CTA</h2>
        </Container>
        <BlockCTA entity={mockCTAEntity as any} />
      </div>

      {/* ================================================================ */}
      {/* PARAGRAPHS */}
      {/* ================================================================ */}

      <div className="Paragraph">
        <Container>
          <h2>Paragraph: Text</h2>
        </Container>
        <ParagraphText entity={mockParagraphTextEntity as any} />
      </div>

      <div className="Paragraph">
        <Container>
          <h2>Paragraph: Text + Media</h2>
        </Container>
        <ParagraphTextMedia entity={mockParagraphTextMediaEntity as any} />
      </div>

      <div className="Paragraph">
        <Container>
          <h2>Paragraph: Quote</h2>
        </Container>
        <ParagraphQuote entity={mockParagraphQuoteEntity as any} />
      </div>

      <div className="Paragraph">
        <Container>
          <h2>Paragraph: Information</h2>
        </Container>
        <ParagraphInformation entity={mockParagraphInformationEntity as any} />
      </div>

      <div className="Paragraph">
        <Container>
          <h2>Paragraph: FAQ</h2>
        </Container>
        <ParagraphFAQ entity={mockParagraphFAQEntity as any} />
      </div>

      <div className="Paragraph">
        <Container>
          <h2>Paragraph: Downloads</h2>
        </Container>
        <ParagraphDownloads entity={mockParagraphDownloadsEntity as any} />
      </div>

      <div className="Paragraph">
        <Container>
          <h2>Paragraph: USP</h2>
        </Container>
        <ParagraphUSP entity={mockParagraphUSPEntity as any} />
      </div>

      <div className="Paragraph">
        <Container>
          <h2>Paragraph: USP Subparagraph</h2>
        </Container>
        <ParagraphUSPSubparagraph entity={mockParagraphUSPSubparagraphEntity as any} />
      </div>

      <div className="Paragraph">
        <Container>
          <h2>Paragraph: Text + Image Grid</h2>
        </Container>
        <ParagraphTextImageGrid entity={mockParagraphTextImageGridEntity as any} />
      </div>

      {/* ================================================================ */}
      {/* NODES */}
      {/* ================================================================ */}

      <div className="Paragraph">
        <Container>
          <h2>Node: Article Teaser</h2>
          <h3>Default (responsive)</h3>
        </Container>
        <Container>
          <div className="row">
            <div className="col-12 col-lg-6">
              <NodeArticleTeaser entity={mockArticleEntity as any} />
            </div>
            <div className="col-12 col-lg-6">
              <NodeArticleTeaser entity={mockArticleEntity as any} />
            </div>
          </div>
        </Container>

        <Container>
          <h3>Highlighted (large)</h3>
        </Container>
        <Container>
          <NodeArticleTeaser entity={mockArticleEntity as any} highlighted large />
        </Container>
      </div>

      <div className="Paragraph">
        <Container>
          <h2>Node: Document Teaser</h2>
          <h3>Publicatie (small)</h3>
        </Container>
        <Container>
          <div className="row">
            <div className="col-12 col-md-6 col-lg-4">
              <NodeDocumentTeaser entity={mockDocumentEntity as any} />
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <NodeDocumentTeaser entity={mockDocumentEntity as any} />
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <NodeDocumentTeaser entity={mockDocumentEntity as any} />
            </div>
          </div>
        </Container>

        <Container>
          <h3>Magazine (big)</h3>
        </Container>
        <Container>
          <div className="row">
            <div className="col-12 col-lg-6">
              <NodeDocumentTeaser entity={mockMagazineEntity as any} />
            </div>
          </div>
        </Container>
      </div>

      <div className="Paragraph">
        <Container>
          <h2>Node: Partner Teaser</h2>
        </Container>
        <Container>
          <div className="row">
            <div className="col-12 col-md-6 col-lg-4">
              <NodePartnerTeaser entity={mockPartnerEntity as any} />
            </div>
          </div>
        </Container>
      </div>

      <div className="Paragraph">
        <Container>
          <h2>Node: Vacancy Teaser</h2>
        </Container>
        <Container>
          <div className="row">
            <div className="col-12 col-lg-6">
              <NodeVacancyTeaser entity={mockVacancyEntity as any} />
            </div>
            <div className="col-12 col-lg-6">
              <NodeVacancyTeaser entity={mockVacancyEntity as any} />
            </div>
          </div>
        </Container>
      </div>

      <div className="Paragraph">
        <Container>
          <h2>Node: Project Teaser</h2>
        </Container>
        <Container>
          <div className="row">
            <div className="col-12 col-sm-6 col-md-4">
              <NodeProjectTeaser entity={mockProjectEntity as any} classes="" />
            </div>
            <div className="col-12 col-sm-6 col-md-4">
              <NodeProjectTeaser entity={mockProjectEntity as any} classes="" />
            </div>
            <div className="col-12 col-sm-6 col-md-4">
              <NodeProjectTeaser entity={mockProjectEntity as any} classes="" />
            </div>
          </div>
        </Container>
      </div>

      <div className="Paragraph">
        <Container>
          <h2>Node: Event</h2>
        </Container>
        <Container>
          <NodeEventPage entity={mockEventEntity as any} />
        </Container>
      </div>

      {/* ================================================================ */}
      {/* PROFILE */}
      {/* ================================================================ */}

      <div className="Paragraph">
        <Container>
          <h2>Profile: Default Teaser</h2>
        </Container>
        <Container>
          <div className="row">
            <div className="col-12 col-md-6 col-lg-4">
              <DefaultProfileTeaser entity={mockProfileEntity as any} classes="" />
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <DefaultProfileTeaser entity={mockProfileEntity as any} classes="" />
            </div>
          </div>
        </Container>
      </div>

      {/* ================================================================ */}
      {/* TAXONOMY */}
      {/* ================================================================ */}

      <div className="Paragraph">
        <Container>
          <h2>Taxonomy: Theme Teaser</h2>
        </Container>
        <Container>
          <div className="row">
            <div className="col-12 col-md-6 col-lg-4">
              <TaxonomyThemeTeaser entity={mockThemeEntity as any} classes="" />
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <TaxonomyThemeTeaser entity={mockThemeEntity as any} classes="" />
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <TaxonomyThemeTeaser entity={mockThemeEntity as any} classes="" />
            </div>
          </div>
        </Container>
      </div>
    </article>
  );
};

export default DesignSystem;
